<template>
  <div class="modal-overlay" v-if="visible" @click.self="close">
    <div class="modal-box">
      <h3>{{ title }}</h3>
      <!-- 批量平仓模式 -->
      <div v-if="mode === 'closeAll'" class="batch-info">
        <p>将对以下 <strong>{{ positionCount }}</strong> 个持仓进行平仓</p>
        <div class="batch-summary">
          <div v-for="grp in groupPositions" :key="grp.symbol" class="summary-row">
            <span class="summary-sym">{{ grp.symbolName || grp.symbol }}</span>
            <span class="summary-lots">{{ grp.count }}笔 / {{ grp.totalLots }}手</span>
            <span class="summary-price">市价 {{ grp.marketPrice }}</span>
            <span :class="['summary-pnl', grp.totalPnl >= 0 ? 'pnl-plus' : 'pnl-minus']">{{ grp.totalPnl >= 0 ? '+' : '' }}{{ grp.totalPnl }}</span>
          </div>
        </div>
      </div>
      <!-- 单个平仓 -->
      <div v-if="mode === 'close' && position" class="pos-info">
        <p>
          <span :class="['dir-tag', position.direction === 'buy' ? 'tag-buy' : 'tag-sell']">
            {{ position.direction === 'buy' ? '买入' : '卖出' }}
          </span>
          {{ position.symbol }} {{ position.lots }}手
        </p>
        <p class="pos-price">开仓价：{{ position.openPrice }} · 浮动盈亏：<span :class="pnlClass">{{ position.pnl }}</span></p>
      </div>
      <!-- 撤单模式 -->
      <div v-if="mode === 'cancel' && position" class="pos-info">
        <p>
          <span :class="['dir-tag', position.direction === 'buy' ? 'tag-buy' : 'tag-sell']">
            {{ position.direction === 'buy' ? '买入' : '卖出' }}
          </span>
          {{ position.symbol }} {{ position.lots }}手 · 挂单价：{{ position.price }}
        </p>
      </div>
      <div class="modal-btns">
        <button class="cancel" :disabled="confirming" @click="close">取消</button>
        <button class="confirm" :disabled="confirming" @click="doAction">{{ confirming ? '处理中...' : confirmText }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { showToast } from '@/utils/common'

export default {
  name: 'ClosePositionModal',
  props: {
    visible: Boolean,
    position: Object, // 单个持仓/挂单对象，null时为批量模式
    positionCount: { type: Number, default: 0 },
    mode: { type: String, default: 'close' } // 'close' | 'closeAll' | 'cancel'
  },
  data() {
    return {
      confirming: false
    }
  },
  computed: {
    title() {
      if (this.mode === 'closeAll') return '一键平仓'
      if (this.mode === 'cancel') return '撤单确认'
      return '平仓确认'
    },
    confirmText() {
      if (this.mode === 'closeAll') return '确认全部平仓'
      if (this.mode === 'cancel') return '确认撤单'
      return '确认平仓'
    },
    pnlClass() {
      if (!this.position) return ''
      const pnl = parseFloat(this.position.pnl) || 0
      return pnl >= 0 ? 'pnl-plus' : 'pnl-minus'
    },
    /** 按品种分组统计持仓 */
    groupPositions() {
      const positions = this.$store.state.trade.positions || []
      const marketData = this.$store.state.trade.marketData || {}
      const map = {}
      for (const pos of positions) {
        if (!map[pos.symbol]) {
          const md = marketData[pos.symbol] || {}
          map[pos.symbol] = {
            symbol: pos.symbol,
            symbolName: pos.symbolName || pos.symbol,
            count: 0,
            totalLots: 0,
            totalPnl: 0,
            marketPrice: md.price ? (pos.symbol === 'XAUUSD' ? md.price.toFixed(2) : md.price.toFixed(3)) : '—'
          }
        }
        map[pos.symbol].count++
        map[pos.symbol].totalLots += pos.lots
        map[pos.symbol].totalPnl += (pos.pnl || 0)
      }
      return Object.values(map)
    }
  },
  methods: {
    close() { this.$emit('close') },
    async doAction() {
      if (this.confirming) return
      this.confirming = true
      try {
        if (this.mode === 'closeAll') {
          // 批量平仓：并行执行（后端已加行锁保证余额更新串行化，安全），单个失败不阻断其他
          const positions = this.$store.state.trade.positions || []
          const results = await Promise.allSettled(positions.map(pos =>
            this.$store.dispatch('trade/closePosition', { orderId: pos.id })
          ))
          const failCount = results.filter(r => r.status === 'rejected').length
          showToast(failCount ? `平仓完成，${failCount} 笔失败` : '全部平仓成功')
        } else if (this.mode === 'cancel') {
          // 撤单
          await this.$store.dispatch('trade/cancelPending', {
            orderId: this.position.id
          })
          showToast('撤单成功')
        } else {
          // 单个平仓
          await this.$store.dispatch('trade/closePosition', {
            orderId: this.position.id
          })
          showToast('平仓成功')
        }
        this.$emit('closed')
        this.close()
      } catch (err) {
        showToast(err.message || '操作失败')
      } finally {
        this.confirming = false
      }
    }
  },
  watch: {
    visible() {
      // reset state when modal opens
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-box { h3 { margin-bottom: 16px; } }
.batch-info { background: var(--bg); border-radius: 6px; padding: 10px 14px; margin-bottom: 12px; font-size: 13px; }
.batch-summary { margin-top: 8px; border-top: 1px solid var(--border); padding-top: 8px; }
.summary-row { display: flex; align-items: center; gap: 10px; font-size: 13px; padding: 4px 0; }
.summary-sym { font-weight: 600; min-width: 50px; }
.summary-lots { color: var(--text-secondary); }
.summary-price { margin-left: auto; }
.summary-pnl { min-width: 70px; text-align: right; }
.pos-info { margin-bottom: 12px; font-size: 14px; line-height: 1.8; }
.dir-tag { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 12px; color: #fff; margin-right: 6px; }
.tag-buy { background: #e64340; }
.tag-sell { background: #07c160; }
.pos-price { font-size: 13px; color: var(--text-secondary); }
.pnl-plus { color: #e64340; }
.pnl-minus { color: #07c160; }
.modal-btns { display: flex; gap: 12px; margin-top: 16px; button { flex: 1; padding: 10px; border-radius: 6px; border: none; cursor: pointer; font-family: inherit; font-weight: 600; } .cancel { background: var(--bg); } .confirm { background: var(--red); color: #fff; } }
</style>
